import React from 'react';
import { Drawer, Typography, Button, Badge } from '@crisellevaldez/speculo-ui';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  SkipForward,
  Download,
  Circle,
} from 'lucide-react';
import { useDeactivationTracker } from '../../../../contexts/deactivation/DeactivationTrackerContext';
import {
  DEACTIVATION_STEP_ORDER,
  type DeactivationStepName,
  type DeactivationStepResult,
  type DeactivationStatus,
} from '../../../../api/organizationStatus';

const STEP_LABELS: Record<DeactivationStepName, string> = {
  cancel_scheduled_calls: 'Cancel scheduled calls & pause flows',
  cancel_scheduled_emails: 'Cancel scheduled emails',
  disable_email_features: 'Disable email features',
  takedown_idx_domain: 'Take down IDX domain & hosting',
  delete_sendgrid_sender: 'Delete SendGrid sender identity',
  revoke_user_access: 'Revoke user access',
};

const StatusIcon: React.FC<{
  status?: DeactivationStepResult['status'];
  /** Step hasn't run yet — pipeline is still queued/processing and this step has no result. */
  inFlight: boolean;
}> = ({ status, inFlight }) => {
  if (status === 'success')
    return <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />;
  if (status === 'skipped') return <SkipForward className="w-4 h-4 text-muted-foreground" />;
  if (status === 'error')
    return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
  if (inFlight)
    return <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />;
  // Terminal state but no result reported for this step (shouldn't happen — keep neutral).
  return <Circle className="w-4 h-4 text-muted-foreground/60" />;
};

const overallBadge = (status: DeactivationStatus | undefined) => {
  switch (status) {
    case 'queued':
      return {
        label: 'Queued',
        tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
      };
    case 'processing':
      return {
        label: 'Processing',
        tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
      };
    case 'completed':
      return {
        label: 'Completed',
        tone: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
        icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      };
    case 'completed_with_errors':
      return {
        label: 'Completed with errors',
        tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
        icon: <AlertTriangle className="w-3.5 h-3.5" />,
      };
    case 'failed':
      return {
        label: 'Failed',
        tone: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        icon: <XCircle className="w-3.5 h-3.5" />,
      };
    default:
      return {
        label: 'Starting…',
        tone: 'bg-gray-100 text-gray-800 dark:bg-white/10 dark:text-gray-200',
        icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />,
      };
  }
};

export const DeactivationProgressDrawer: React.FC = () => {
  const { active, status, isDrawerOpen, closeDrawer, isTerminal, dismiss } = useDeactivationTracker();

  const handleDownloadResults = () => {
    if (!status || !active) return;
    const payload = JSON.stringify(status, null, 2);
    const blob = new Blob([payload], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ts = (status.completed_at || status.started_at || new Date().toISOString())
      .replace(/[:.]/g, '-');
    const safeOrg = active.orgName.replace(/[^a-z0-9_-]+/gi, '_');
    a.download = `deactivation_${safeOrg}_${ts}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!active) return null;

  const steps = status?.steps ?? {};
  const overall = overallBadge(status?.status);
  const hasErrors = status?.status === 'completed_with_errors' || status?.status === 'failed';

  return (
    <Drawer open={isDrawerOpen} onClose={closeDrawer} size="lg" side="right">
      <Drawer.Header>
        <Typography variant="h4" className="font-bold">
          Deactivating {active.orgName}
        </Typography>
      </Drawer.Header>
      <Drawer.Body className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge className={`${overall.tone} flex items-center gap-1.5`}>
            {overall.icon}
            {overall.label}
          </Badge>
          {status?.started_at && (
            <span className="text-xs text-muted-foreground">
              Started {new Date(status.started_at).toLocaleString()}
            </span>
          )}
        </div>

        {hasErrors && (
          <Typography variant="p" className="text-sm text-muted-foreground">
            Some cleanup steps reported errors. Click <span className="font-medium text-foreground">Download results</span> and send the file to the dev team.
          </Typography>
        )}

        {status?.error && (
          <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 p-3">
            <p className="text-sm font-medium text-red-800 dark:text-red-200">Pipeline error</p>
            <p className="text-xs text-red-700 dark:text-red-300 mt-1 break-words">{status.error}</p>
          </div>
        )}

        <ol className="space-y-2">
          {DEACTIVATION_STEP_ORDER.map((stepName) => {
            const result = steps[stepName];
            const pipelineFinished =
              status?.status === 'completed' ||
              status?.status === 'completed_with_errors' ||
              status?.status === 'failed';
            const inFlight = !result && !pipelineFinished;
            const countEntries = result ? Object.entries(result.counts ?? {}) : [];
            return (
              <li
                key={stepName}
                className="flex gap-3 rounded-md border border-foreground/10 px-3 py-2"
              >
                <div className="pt-0.5">
                  <StatusIcon status={result?.status} inFlight={inFlight} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{STEP_LABELS[stepName]}</p>
                  {result && countEntries.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {countEntries.map(([k, v]) => (
                        <span key={k} className="text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{v}</span> {k.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                  {result?.errors && result.errors.length > 0 && (
                    <ul className="mt-1 list-disc list-inside space-y-0.5">
                      {result.errors.map((err, i) => (
                        <li key={i} className="text-xs text-red-700 dark:text-red-300 break-words">
                          {err}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </Drawer.Body>
      <Drawer.Footer>
        <div className="flex justify-end gap-2 w-full">
          {isTerminal && hasErrors && (
            <Button variant="outline" onClick={handleDownloadResults}>
              <Download className="w-4 h-4 mr-2" />
              Download results
            </Button>
          )}
          {isTerminal ? (
            <Button onClick={dismiss}>Dismiss</Button>
          ) : (
            <Button variant="outline" onClick={closeDrawer}>
              Close
            </Button>
          )}
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};
